import { useMemo, useState } from 'react'
import { MapPin, ChevronDown, PenLine, Check } from 'lucide-react'
import { BottomSheet } from '@/shared/ui/bottom-sheet'
import { SearchField } from '@/shared/ui/search-field'
import { mockClubs } from '@/shared/mock/data'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { useAllPlaces, mockToDirectoryClub, type DirectoryClub } from './use-places'
import { recordPlaceRequest } from './api/place-requests-repository'
import { useAuthStore } from '@/core/auth/auth-store'
import { useMyProfile } from '@/features/perfil/use-my-profile'

// Club-first place selection (docs/usersmatch.md Location Model): pick a court
// from the places database (preferred — 1–2 taps) OR fall back to a manual
// address. Design stub over mockClubs; swaps to the places_courts query later.
export type PlaceSelection =
  | { kind: 'club'; id?: string; name: string }
  | { kind: 'address'; address: string }
  | null

export function PlacePicker({
  value,
  onChange,
}: {
  value: PlaceSelection
  onChange: (place: PlaceSelection) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  const cityForRequest = useMyProfile().data?.location.city ?? null
  const [manual, setManual] = useState(false)
  const [manualValue, setManualValue] = useState(value?.kind === 'address' ? value.address : '')

  function commitManual() {
    const address = manualValue.trim()
    if (address) {
      onChange({ kind: 'address', address })
      // A player typing a place by hand IS the signal that the directory is
      // missing it — and it was being thrown away. Recorded here rather than
      // behind a "sugerir club" button nobody would press: this captures
      // DEMAND (a place three matches were played at) instead of opinions.
      // Fire-and-forget on purpose — the repository swallows its own failures,
      // because a suggestion must never break the match the player is creating.
      if (!AUTH_BYPASS && uid) {
        void recordPlaceRequest({ typed: address, requesterCity: cityForRequest ?? null, uid })
      }
    }
    setOpen(false)
    setManual(false)
  }

  // Dismissing the sheet ANY way (X, backdrop, swipe) keeps a typed address —
  // silently discarding a draft was an audited defect (2026-07-04).
  function closeSheet() {
    if (manual && manualValue.trim()) {
      commitManual()
      return
    }
    setOpen(false)
    setManual(false)
  }

  // Real auth searches the places_courts directory; the bypass keeps the
  // mock fixture for design walks.
  const directory = useAllPlaces()
  const clubs = useMemo(() => {
    const haystack: readonly DirectoryClub[] = AUTH_BYPASS
      ? mockClubs.map(mockToDirectoryClub)
      : (directory.data ?? [])
    const q = query.trim().toLowerCase()
    if (!q) return haystack
    return haystack.filter((c) => c.name.toLowerCase().includes(q) || c.address.toLowerCase().includes(q))
  }, [query, directory.data])

  const label =
    value?.kind === 'club' ? value.name : value?.kind === 'address' ? value.address : null

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        // Explicit name: the wrapping <label> would otherwise collapse this
        // button's accessible name to just "Lugar" (cert-walk-12 a11y finding).
        aria-label={`Lugar: ${label ?? 'elige club o dirección'}`}
        className="field field--icon w-full flex items-center gap-2 text-left"
      >
        <span className="absolute left-3 top-1/2 -translate-y-1/2 z-10 text-[color:var(--fg-faint)] pointer-events-none">
          <MapPin size={18} />
        </span>
        <span className={`flex-1 truncate ${label ? '' : 'text-[color:var(--fg-muted)]'}`}>
          {label ?? 'Elige club o dirección'}
        </span>
        <ChevronDown size={16} className="shrink-0 text-[color:var(--fg-faint)]" />
      </button>

      <BottomSheet open={open} onClose={closeSheet} title="Lugar">
        {manual ? (
          <div className="flex flex-col gap-3 pb-2">
            <input
              className="field"
              autoFocus
              placeholder="Calle, número, ciudad"
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && commitManual()}
            />
            <button
              type="button"
              disabled={!manualValue.trim()}
              onClick={commitManual}
              className="flex items-center justify-center gap-2 min-h-[var(--tap)] rounded-[var(--radius)] font-semibold bg-[var(--accent)] text-[color:var(--accent-fg)] disabled:opacity-40 active:scale-[0.99] transition-transform"
            >
              <Check size={17} /> Usar esta dirección
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3 pb-2">
            <SearchField value={query} onChange={setQuery} placeholder="Buscar club" />
            <div className="flex flex-col">
              {clubs.map((c) => {
                const active = value?.kind === 'club' && value.name === c.name
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      onChange({ kind: 'club', id: c.id, name: c.name })
                      setOpen(false)
                    }}
                    className="flex items-center gap-3 min-h-[var(--tap)] px-1 rounded-[var(--radius)] hover:bg-[var(--line-soft)] active:scale-[0.99] transition-transform text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{c.name}</p>
                      <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)] truncate">
                        {/* from the city centroid, not the player — see use-places.ts */}
                        {c.address}{c.distanceKm != null ? ` · a ${c.distanceKm} km del centro` : ''}
                      </p>
                    </div>
                    {active && <Check size={18} className="shrink-0 text-[color:var(--accent)]" />}
                  </button>
                )
              })}
              {/* A FAILED READ IS NOT AN EMPTY DIRECTORY. `directory.data ?? []`
                  makes both look identical, so a dropped connection told the
                  player their club does not exist — on the Crear flow, where
                  the next thing they do is type the address by hand. Since
                  2026-08-10 that hand-typed text also records a place request,
                  so the lie would have quietly polluted the operator inbox with
                  clubs that were in the directory all along. */}
              {directory.isError ? (
                <div className="py-3 px-1 flex flex-col gap-2">
                  <p className="text-[color:var(--fg-muted)]">
                    No pudimos cargar los clubes.
                  </p>
                  <button
                    type="button"
                    onClick={() => void directory.refetch()}
                    className="self-start min-h-[var(--tap)] font-semibold text-[color:var(--accent)]"
                  >
                    Reintentar
                  </button>
                </div>
              ) : (
                clubs.length === 0 && (
                  <p className="py-3 px-1 text-[color:var(--fg-muted)]">Ningún club coincide.</p>
                )
              )}
            </div>
            <button
              type="button"
              onClick={() => setManual(true)}
              className="flex items-center gap-3 min-h-[var(--tap)] px-1 rounded-[var(--radius)] text-[color:var(--accent)] hover:bg-[var(--line-soft)] active:scale-[0.99] transition-transform"
            >
              <PenLine size={18} />
              <span className="font-semibold">Escribir otra dirección</span>
            </button>
          </div>
        )}
      </BottomSheet>
    </>
  )
}
